import React, { useContext, useEffect, useMemo, useState } from "react";

import { useTranslation } from 'react-i18next';
import { 
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, IconButton, Alert, CircularProgress,
  Grid, Card, CardContent, CardActions, Checkbox, FormGroup, FormControlLabel,
  Tabs, Tab, Select, MenuItem, InputLabel, FormControl
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { fetchJson } from "../../../utils/fetchJson";
import { UserContext } from "../../../contexts/UserContext";

// Features configuration
const ALL_FEATURES = [
  { id: 'studentManagement', label: 'Student Management' },
  { id: 'finance', label: 'Finance Module' },
  { id: 'reports', label: 'Advanced Reports' },
  { id: 'api', label: 'API Access' },
  { id: 'support', label: '24/7 Support' }
];

export default function SubscriptionPlansPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [form, setForm] = useState({ 
    name: "", 
    description: "", 
    billingCycle: "monthly",
    pricing: { flatFee: 0, perUser: 0, perStudentMonthly: 0, perStudentQuarterly: 0, perStudentAnnual: 0 },
    features: {}
  });
  const [snack, setSnack] = useState("");
  const [tabValue, setTabValue] = useState(0);

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('billing');
  }, [user]);

  useEffect(() => { loadPlans(); }, []);

  async function loadPlans() {
    setLoading(true); setError("");
    try {
      const data = await fetchJson("/subscription-plans");
      setPlans(data);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }

  function openCreate() {
    if (!canManage) return;
    setCurrentPlan(null);

    setForm({ 
      name: "", 
      description: "", 
      billingCycle: "monthly",
      pricing: { flatFee: 0, perUser: 0, perStudentMonthly: 0, perStudentQuarterly: 0, perStudentAnnual: 0 },
      features: ALL_FEATURES.reduce((acc, f) => ({ ...acc, [f.id]: false }), {})
    });
    setDialogOpen(true);
  }

  function openEdit(plan) {
    if (!canManage) return;
    setCurrentPlan(plan);

    setForm({
      ...plan,
      pricing: {
        flatFee: 0,
        perUser: 0,
        perStudentMonthly: 0,
        perStudentQuarterly: 0,
        perStudentAnnual: 0,
        ...(plan.pricing || {})
      },
      features: { ...(plan.features || {}) }
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!canManage) return;
    setError("");
    if (!form.name) { setError(t('subscriptionPlans.nameRequired', 'Plan name required')); return; }

    try {
      const url = currentPlan 
        ? `/subscription-plans/${currentPlan.id}`
        : "/subscription-plans";
      
      const method = currentPlan ? "PUT" : "POST";
      
      await fetchJson(url, {
        method, 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      
      setSnack(currentPlan ? t('subscriptionPlans.updated', 'Plan updated') : t('subscriptionPlans.created', 'Plan created'));
      setDialogOpen(false);
      loadPlans();
    } catch (e) { 
      setError(e.message); 
    }
  }

  async function handleDelete(id) {
    if (!canManage) return;
    if (!window.confirm(t('subscriptionPlans.confirmDelete', 'Delete this plan?'))) return;
    try {
      await fetchJson(`/subscription-plans/${id}`, { method: "DELETE" });
      setSnack(t('subscriptionPlans.deleted', 'Plan deleted'));
      loadPlans();
    } catch (e) { alert(e.message); }
  }

  function toggleFeature(featureId, checked) {
    setForm(f => ({
      ...f,
      features: { ...f.features, [featureId]: checked }
    }));
  }

  function updatePricing(field, value) {
    setForm(f => ({
      ...f,
      pricing: { ...f.pricing, [field]: parseFloat(value) || 0 }
    }));
  }

  const columns = [
    { field: "id", headerName: t('subscriptionPlans.id', 'ID'), width: 70 },
    { field: "name", headerName: t('subscriptionPlans.planName', 'Plan Name'), flex: 1 },
    { field: "billingCycle", headerName: t('subscriptionPlans.billing', 'Billing'), width: 120 },
    { 
      field: "pricing", 
      headerName: t('subscriptionPlans.pricing', 'Pricing'), 
      width: 180,
      valueGetter: ({ value }) => {
        const v = value || {};
        const monthly = Number(v.perStudentMonthly || 0);
        const quarterly = Number(v.perStudentQuarterly || 0);
        const annual = Number(v.perStudentAnnual || 0);
        if (annual > 0) return `$${annual}/student (annual)`;
        if (quarterly > 0) return `$${quarterly}/student (quarterly)`;
        if (monthly > 0) return `$${monthly}/student (monthly)`;
        return `$${Number(v.flatFee || 0)} + $${Number(v.perUser || 0)}/user`;
      }
    },
    { 
      field: "isActive", 
      headerName: t('subscriptionPlans.active', 'Active'), 
      width: 100, 
      type: "boolean" 
    },
    {
      field: "actions",
      headerName: t('subscriptionPlans.actions', 'Actions'),
      width: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={() => openEdit(row)} startIcon={<EditIcon />} disabled={!canManage}>
            {t('subscriptionPlans.edit', 'Edit')}
          </Button>
          <Button 
            size="small" 
            color="error" 
            onClick={() => handleDelete(row.id)} 
            startIcon={<DeleteIcon />}
            disabled={!canManage}
          >
            {t('subscriptionPlans.delete', 'Delete')}
          </Button>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" gutterBottom>{t('subscriptionPlans.title', 'Subscription Plans')}</Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ mb: 2 }} disabled={!canManage}>
        {t('subscriptionPlans.createPlan', 'Create New Plan')}
      </Button>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid 
            rows={plans} 
            columns={columns} 
            pageSize={10}
            disableSelectionOnClick
          />
        </Box>
      )}

      {/* Plan Editor Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          {currentPlan ? t('subscriptionPlans.editPlan', 'Edit Subscription Plan') : t('subscriptionPlans.createPlan', 'Create New Plan')}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label={t('subscriptionPlans.basicInfo', 'Basic Info')} />
            <Tab label={t('subscriptionPlans.features', 'Features')} />
            <Tab label={t('subscriptionPlans.pricing', 'Pricing')} />
          </Tabs>
          
          {tabValue === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('subscriptionPlans.planName', 'Plan Name')}
                  fullWidth
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('subscriptionPlans.description', 'Description')}
                  fullWidth
                  multiline
                  rows={3}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('subscriptionPlans.billingCycle', 'Billing Cycle')}</InputLabel>
                  <Select
                    value={form.billingCycle}
                    onChange={e => setForm(f => ({ ...f, billingCycle: e.target.value }))}
                    label={t('subscriptionPlans.billingCycle', 'Billing Cycle')}
                  >
                    <MenuItem value="monthly">{t('subscriptionPlans.monthly', 'Monthly')}</MenuItem>
                    <MenuItem value="quarterly">{t('subscriptionPlans.quarterly', 'Quarterly')}</MenuItem>
                    <MenuItem value="annual">{t('subscriptionPlans.annual', 'Annual')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          
          {tabValue === 1 && (
            <FormGroup>
              <Typography variant="h6" gutterBottom>
                {t('subscriptionPlans.planFeatures', 'Plan Features')}
              </Typography>
              <Grid container spacing={2}>
                {ALL_FEATURES.map(feature => (
                  <Grid item xs={12} sm={6} key={feature.id}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={!!form.features[feature.id]} 
                          onChange={e => toggleFeature(feature.id, e.target.checked)}
                        />
                      }
                      label={feature.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          )}
          
          {tabValue === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('subscriptionPlans.perStudentMonthly', 'Per Student Monthly ($)')}
                  type="number"
                  fullWidth
                  value={form.pricing.perStudentMonthly}
                  onChange={e => updatePricing('perStudentMonthly', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('subscriptionPlans.perStudentQuarterly', 'Per Student Quarterly ($)')}
                  type="number"
                  fullWidth
                  value={form.pricing.perStudentQuarterly}
                  onChange={e => updatePricing('perStudentQuarterly', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('subscriptionPlans.perStudentAnnual', 'Per Student Annual ($)')}
                  type="number"
                  fullWidth
                  value={form.pricing.perStudentAnnual}
                  onChange={e => updatePricing('perStudentAnnual', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('subscriptionPlans.flatFee', 'Flat Fee ($)')}
                  type="number"
                  fullWidth
                  value={form.pricing.flatFee}
                  onChange={e => updatePricing('flatFee', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('subscriptionPlans.perUserFee', 'Per User Fee ($)')}
                  type="number"
                  fullWidth
                  value={form.pricing.perUser}
                  onChange={e => updatePricing('perUser', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {t('subscriptionPlans.pricingNote', 'Total can be based on per-student pricing or a flat fee model.')}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('subscriptionPlans.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!canManage}>
            {t('subscriptionPlans.savePlan', 'Save Plan')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack("")}
        message={snack}
        action={
          <IconButton size="small" color="inherit" onClick={() => setSnack("")}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}